import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  Paper,
  Switch,
  FormControlLabel,
  CircularProgress,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination
} from '@mui/material';
import { 
  Delete,
  Add,
  Save,
  CheckCircle,
  Error,
  Schedule
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { buildApiUrl } from '../config/api';

const AdminNotifications = () => {
  const auth = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  
  // Notification settings state
  const [settings, setSettings] = useState({
    notifications_enabled: true,
    frequency: 'immediate',
    recipient_emails: [],
    include_response_text: true,
    include_participant_metadata: true,
    include_admin_links: true,
    anonymize_participant_data: false,
    email_subject_template: '',
    email_body_template: '',
    max_emails_per_hour: 100
  });
  
  const [newEmail, setNewEmail] = useState('');
  
  // Notification history state
  const [notificationLogs, setNotificationLogs] = useState([]);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(10);
  const [historyFilter, setHistoryFilter] = useState('all');

  const loadNotificationSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(buildApiUrl('admin/email/settings'), {
        headers: { 'Authorization': `Bearer ${auth.token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      } else {
        throw new Error('Failed to load notification settings');
      }
    } catch (error) {
      console.error('Failed to load notification settings:', error);
      setMessage({ text: 'Falha ao carregar configurações de notificação', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [auth.token]);

  const loadNotificationHistory = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        skip: historyPage * historyRowsPerPage,
        limit: historyRowsPerPage
      };
      if (historyFilter !== 'all') {
        params.status = historyFilter;
      }
      
      const queryParams = new URLSearchParams(params).toString();
      const response = await fetch(buildApiUrl(`admin/email/notifications?${queryParams}`), {
        headers: { 'Authorization': `Bearer ${auth.token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNotificationLogs(data);
      } else {
        throw new Error('Failed to load notification history');
      }
    } catch (error) {
      console.error('Failed to load notification history:', error);
      setMessage({ text: 'Falha ao carregar histórico de notificações', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [auth.token, historyPage, historyRowsPerPage, historyFilter]);

  // Load notification settings
  useEffect(() => {
    loadNotificationSettings();
    if (activeTab === 1) {
      loadNotificationHistory();
    }
  }, [activeTab, loadNotificationHistory, loadNotificationSettings]);

  const handleSettingChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addRecipientEmail = () => {
    if (!newEmail) return;
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setMessage({ text: 'Por favor, insira um endereço de email válido', type: 'error' });
      return;
    }
    
    if (settings.recipient_emails.includes(newEmail)) {
      setMessage({ text: 'Este email já está na lista', type: 'error' });
      return;
    }
    
    handleSettingChange('recipient_emails', [...settings.recipient_emails, newEmail]);
    setNewEmail('');
  };

  const removeRecipientEmail = (email) => {
    handleSettingChange(
      'recipient_emails', 
      settings.recipient_emails.filter(e => e !== email)
    );
  };

  const saveSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch(buildApiUrl('admin/email/settings'), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${auth.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save notification settings');
      }
      setMessage({ text: 'Configurações de notificação salvas com sucesso', type: 'success' });
    } catch (error) {
      setMessage({ 
        text: error.message || 'Falha ao salvar configurações de notificação', 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <CheckCircle color="success" fontSize="small" />;
      case 'failed':
        return <Error color="error" fontSize="small" />;
      case 'pending':
      case 'retrying':
        return <Schedule color="warning" fontSize="small" />;
      default:
        return null;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  // Check if user is loaded and admin
  if (!auth.isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  
  if (auth.user === null) {
    // Still loading user data
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading...</Typography>
      </Container>
    );
  }
  
  if (auth.user && auth.user.role !== 'ADMIN') {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Email Notifications
        </Typography>

        {message.text && (
          <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage({ text: '', type: '' })}>
            {message.text}
          </Alert>
        )}

        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
          <Tab label="Configurações" />
          <Tab label="Histórico" />
        </Tabs>

        {/* Settings Tab */}
        {activeTab === 0 && (
          <>
            {/* Notification Settings */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Configurações Gerais
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.notifications_enabled}
                        onChange={(e) => handleSettingChange('notifications_enabled', e.target.checked)}
                      />
                    }
                    label="Habilitar notificações por email para novas submissões"
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Frequência de Notificação</InputLabel>
                    <Select
                      value={settings.frequency}
                      onChange={(e) => handleSettingChange('frequency', e.target.value)}
                      label="Frequência de Notificação"
                    >
                      <MenuItem value="immediate">Imediato</MenuItem>
                      <MenuItem value="hourly" disabled>Resumo por Hora (Em Breve)</MenuItem>
                      <MenuItem value="daily" disabled>Resumo Diário (Em Breve)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Máximo de Emails por Hora"
                    type="number"
                    value={settings.max_emails_per_hour}
                    onChange={(e) => handleSettingChange('max_emails_per_hour', parseInt(e.target.value))}
                    helperText="Limite de taxa para prevenir spam"
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Recipients */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Endereços de Email dos Destinatários
              </Typography>
              
              <Box display="flex" gap={2} mb={2}>
                <TextField
                  label="Adicionar Endereço de Email"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addRecipientEmail()}
                  placeholder="pesquisador@exemplo.com"
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="contained"
                  onClick={addRecipientEmail}
                  startIcon={<Add />}
                >
                  Adicionar
                </Button>
              </Box>
              
              {settings.recipient_emails.length === 0 ? (
                <Alert severity="info">
                  Nenhum email de destinatário configurado. Adicione endereços de email para receber notificações.
                </Alert>
              ) : (
                <List>
                  {settings.recipient_emails.map((email, index) => (
                    <ListItem key={index} divider>
                      <ListItemText primary={email} />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => removeRecipientEmail(email)}
                        >
                          <Delete />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>

            {/* Email Content Settings */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Configurações de Conteúdo do Email
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.include_response_text}
                        onChange={(e) => handleSettingChange('include_response_text', e.target.checked)}
                      />
                    }
                    label="Incluir texto da resposta no email"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.include_participant_metadata}
                        onChange={(e) => handleSettingChange('include_participant_metadata', e.target.checked)}
                      />
                    }
                    label="Incluir metadados do participante"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.include_admin_links}
                        onChange={(e) => handleSettingChange('include_admin_links', e.target.checked)}
                      />
                    }
                    label="Incluir links do console administrativo"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.anonymize_participant_data}
                        onChange={(e) => handleSettingChange('anonymize_participant_data', e.target.checked)}
                      />
                    }
                    label="Anonimizar dados do participante"
                  />
                </Grid>
              </Grid>
            </Paper>

            <Box display="flex" gap={2}>
              <Button
                variant="contained"
                onClick={saveSettings}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <Save />}
              >
                Salvar Configurações
              </Button>
            </Box>
          </>
        )}

        {/* History Tab */}
        {activeTab === 1 && (
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Histórico de Notificações
              </Typography>
              
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Filtrar por Status</InputLabel>
                <Select
                  value={historyFilter}
                  onChange={(e) => {
                    setHistoryFilter(e.target.value);
                    setHistoryPage(0);
                  }}
                  label="Filtrar por Status"
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="sent">Enviados</MenuItem>
                  <MenuItem value="failed">Falharam</MenuItem>
                  <MenuItem value="pending">Pendentes</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Destinatário</TableCell>
                    <TableCell>Assunto</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Criado</TableCell>
                    <TableCell>Enviado</TableCell>
                    <TableCell>Tentativas</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : notificationLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        Nenhum log de notificação encontrado
                      </TableCell>
                    </TableRow>
                  ) : (
                    notificationLogs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {getStatusIcon(log.status)}
                            <Typography variant="body2">
                              {log.status}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{log.recipient_email}</TableCell>
                        <TableCell>{log.subject}</TableCell>
                        <TableCell>
                          <Chip 
                            label={log.notification_type} 
                            size="small"
                            color={log.notification_type === 'test_email' ? 'info' : 'default'}
                          />
                        </TableCell>
                        <TableCell>{formatDate(log.created_at)}</TableCell>
                        <TableCell>{formatDate(log.sent_at)}</TableCell>
                        <TableCell>{log.attempts}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              component="div"
              count={-1} // Unknown total count
              page={historyPage}
              onPageChange={(e, newPage) => setHistoryPage(newPage)}
              rowsPerPage={historyRowsPerPage}
              onRowsPerPageChange={(e) => {
                setHistoryRowsPerPage(parseInt(e.target.value, 10));
                setHistoryPage(0);
              }}
            />
          </Paper>
        )}
      </Container>
  );
};

export default AdminNotifications;